import { TFunction } from 'i18next'
import { OrderChannel } from '@afterpaytouch/portal-api/consumer/ordertransactions/types'
import type { LoggedInLoadingMessageKey } from '../state'
import { CardBrand } from '@afterpay/types'
import { CardErrorReason } from '../model'

type LoadingMessages = {
  title: string
  description: string
}

type TranslateFn = (key: string) => string

export const getChannelTranslation = (channel: OrderChannel, t: TFunction): string => {
  switch (channel) {
    case OrderChannel.ONLINE:
      return t('common:channel.online')
    case OrderChannel.IN_STORE:
      return t('common:channel.inStore')
    case OrderChannel.IN_STORE_CARD:
      return t('common:channel.inStoreCard')
    case OrderChannel.IN_STORE_CARD_ONLINE:
      return t('common:channel.inStoreCardOnline')
    case OrderChannel.MONEY:
      return t('common:channel.money')
  }
}

export const getContractAgreementLoadingMessages = (t: TranslateFn, key: LoggedInLoadingMessageKey): LoadingMessages | null => {
  switch (key) {
    case 'unsuitabilityQuestions':
      return {
        title: t('unsuitabilityQuestions:title'),
        description: t('unsuitabilityQuestions:description'),
      }
    case 'verifyIdentity':
      return {
        title: t('verifyIdentity:title'),
        description: t('verifyIdentity:description'),
      }
    default:
      return null
  }
}

interface GetPaymentMethodCardErrorMessageParams {
  cardError: CardErrorReason | null | undefined
  cardBrand?: CardBrand | ''
  cardExpiryLimitInMonths?: number
  isInline?: boolean
  t: TFunction
}

export const getPaymentMethodCardErrorMessage = ({
  cardError,
  cardBrand,
  cardExpiryLimitInMonths = 2,
  isInline = false,
  t,
}: GetPaymentMethodCardErrorMessageParams): string => {
  if (cardBrand === CardBrand.AMEX && cardError === 'invalidBrand') {
    return isInline ? t('paymentMethod:errors.amexUnsupported_inline') : t('paymentMethod:errors.amexUnsupported')
  }

  switch (cardError) {
    case 'invalidBrand':
      return t('paymentMethod:errors.invalidBrand', { selectedCardBrand: cardBrand })
    case 'cardExpiry':
      return t('paymentMethod:errors.cardExpiry')
    case 'achNotAllowed':
      return t('paymentMethod:errors.achNotAllowed')
    case 'creditCardNotAllowed':
      return t('paymentMethod:errors.creditCardNotAllowed')
    case 'cardExpiryLimitInMonths':
      return t('paymentMethod:errors.cardExpiryLimitInMonths', { months: cardExpiryLimitInMonths })
    case 'cashAppCardIneligible':
      return t('paymentMethod:errors.cashAppCardIneligible')
    case 'dssProhibitedPaymentMethod':
      return t('paymentMethod:errors.dssProhibitedPaymentMethod')
    case 'unacceptableCard':
      return t('paymentMethod:errors.unacceptableCard', { selectedCardBrand: cardBrand })
    case 'unacceptableIssuerRestrictedCard':
      return t('paymentMethod:errors.unacceptableIssuerRestrictedCard', { selectedCardBrand: cardBrand })
    case 'fallbackGenericMessage':
    default:
      return t('paymentMethod:errors.fallbackGenericMessage')
  }
}
